import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import SharedLocationForm from '../vedic/forms/LocationForm';

/* ─── Animations ─── */
const fadeIn = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const glow = keyframes`0%{box-shadow:0 0 10px rgba(108,99,255,0.2)}50%{box-shadow:0 0 25px rgba(108,99,255,0.4)}100%{box-shadow:0 0 10px rgba(108,99,255,0.2)}`;
const pulse = keyframes`0%{transform:scale(1)}50%{transform:scale(1.03)}100%{transform:scale(1)}`;

/* ─── Styled Components ─── */
const PageContainer = styled.div`max-width:900px;margin:40px auto;padding:0 20px;`;
const PageTitle = styled.h1`font-size:2rem;text-align:center;margin-bottom:5px;color:${p => p.theme.colors.text.primary};`;
const PageSubtitle = styled.p`text-align:center;color:${p => p.theme.colors.text.secondary};margin-bottom:30px;font-size:0.95rem;`;

const Card = styled.div`
  background:${p => p.theme.colors.surface};border-radius: ${p => p.theme.radius.md}px;box-shadow: ${p => p.theme.shadow.card};padding:30px;
  animation:${fadeIn} 0.3s ease;
`;

const CardTitle = styled.h2`font-size:1.3rem;margin-bottom:20px;color:${p => p.theme.colors.text.primary};text-align:center;`;

const Select = styled.select`
  width:100%;padding:10px 12px;border:1px solid ${p => p.theme.colors.border};border-radius:6px;font-size:0.95rem;
  &:focus{outline:none;border-color:${p => p.theme.colors.primary}}
`;


const BackButton = styled.button`
  background:none;border:1px solid ${p => p.theme.colors.border};border-radius:6px;padding:8px 20px;
  font-size:0.85rem;cursor:pointer;color:${p => p.theme.colors.text.secondary};margin-bottom:20px;
  &:hover{border-color:${p => p.theme.colors.primary};color:${p => p.theme.colors.primary}}
`;

/* ─── Golden Window ─── */
const GoldenCard = styled.div`
  background:linear-gradient(135deg,#ffd700 0%,#ffb300 50%,#ff8f00 100%);
  border-radius:16px;padding:40px;text-align:center;color:${p => p.theme.colors.surface};margin:20px 0;
  animation:${glow} 3s ease infinite, ${pulse} 2s ease infinite;
  box-shadow:0 4px 30px rgba(255,193,7,0.3);
`;
const GoldenIcon = styled.div`font-size:3rem;margin-bottom:12px;`;
const GoldenTitle = styled.div`font-size:1.6rem;font-weight:700;text-shadow:0 1px 3px rgba(0,0,0,0.2);`;
const GoldenTime = styled.div`font-size:2.2rem;font-weight:800;margin:16px 0 8px;text-shadow:0 2px 4px rgba(0,0,0,0.2);`;
const GoldenDuration = styled.div`font-size:1rem;opacity:0.9;`;
const GoldenNote = styled.div`font-size:0.9rem;opacity:0.85;margin-top:16px;font-style:italic;`;

/* ─── Info Grid ─── */
const InfoGrid = styled.div`
  display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:24px;
  @media(max-width:576px){grid-template-columns:1fr}
`;
const InfoCard = styled.div`
  background:${p => p.theme.colors.background};border-radius: ${p => p.theme.radius.md}px;padding:20px;text-align:center;
  border-top:3px solid ${p => p.color || p.theme.colors.primary};animation:${fadeIn} 0.3s ease;
`;
const InfoLabel = styled.div`font-size:0.8rem;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:${p => p.theme.colors.text.muted};`;
const InfoValue = styled.div`font-size:1.3rem;font-weight:700;color:${p => p.theme.colors.text.primary};margin-top:6px;`;

/* ─── Countdown ─── */
const CountdownCard = styled.div`
  background:${p => p.theme.colors.background};border-radius: ${p => p.theme.radius.lg}px;padding:24px;text-align:center;margin-top:24px;
  border:2px dashed ${p => p.theme.colors.primary};
`;
const CountdownLabel = styled.div`font-size:0.85rem;color:${p => p.theme.colors.text.secondary};margin-bottom:8px;font-weight:600;`;
const CountdownTime = styled.div`font-size:2rem;font-weight:800;color:${p => p.theme.colors.primary};font-family:monospace;`;

const langLabels = { en: 'English', ta: 'Tamil' };

/* ─── Component ─── */
const AbhijitMuhurta = () => {
  const [lang, setLang] = useState('en');
  const [place, setPlace] = useState('');
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const timerRef = useRef(null);

  const handlePlaceChange = useCallback((data) => {
    setPlace(data.place);
    setLat(data.lat);
    setLon(data.lon);
    setTimezone(data.timezone);
  }, []);

  const fetchMuhurta = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.get('/vedic/v1/abhijit-muhurta', {
        params: { date, tz: timezone, lang }
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch Abhijit Muhurta');
    } finally {
      setLoading(false);
    }
  }, [date, timezone, lang]);

  // Countdown timer
  useEffect(() => {
    if (!result) { setCountdown(null); return; }

    const startStr = result.abhijit_start || result.start || result.muhurta?.start;
    const endStr = result.abhijit_end || result.end || result.muhurta?.end;
    if (!startStr) return;

    const updateCountdown = () => {
      const now = new Date();
      const today = date || now.toISOString().split('T')[0];

      // Parse time strings (HH:MM or HH:MM:SS)
      const parseTime = (timeStr) => {
        const parts = timeStr.split(':');
        const d = new Date(today);
        d.setHours(parseInt(parts[0]), parseInt(parts[1]), parts[2] ? parseInt(parts[2]) : 0, 0);
        return d;
      };

      try {
        const start = parseTime(startStr);
        const end = parseTime(endStr);

        if (now < start) {
          const diff = start - now;
          const hours = Math.floor(diff / 3600000);
          const mins = Math.floor((diff % 3600000) / 60000);
          const secs = Math.floor((diff % 60000) / 1000);
          setCountdown({
            label: lang === 'ta' ? 'அபிஜித் முஹூர்த்தம் தொடங்க' : 'Abhijit Muhurta starts in',
            time: `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`,
            status: 'upcoming'
          });
        } else if (now >= start && now <= end) {
          const diff = end - now;
          const mins = Math.floor(diff / 60000);
          const secs = Math.floor((diff % 60000) / 1000);
          setCountdown({
            label: lang === 'ta' ? 'அபிஜித் முஹூர்த்தம் நடைபெறுகிறது!' : 'Abhijit Muhurta is ACTIVE!',
            time: `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')} remaining`,
            status: 'active'
          });
        } else {
          setCountdown({
            label: lang === 'ta' ? 'இன்றைய அபிஜித் முஹூர்த்தம் முடிந்தது' : 'Today\'s Abhijit Muhurta has passed',
            time: '',
            status: 'passed'
          });
        }
      } catch {
        setCountdown(null);
      }
    };

    updateCountdown();
    timerRef.current = setInterval(updateCountdown, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [result, date, lang]);

  const am = result?.abhijit_muhurta || {};
  const startTime = am.start || result?.abhijit_start || result?.start || '--';
  const endTime = am.end || result?.abhijit_end || result?.end || '--';
  const duration = am.duration_minutes || result?.duration || '';
  const sunrise = result?.sunrise || '--';
  const sunset = result?.sunset || '--';

  return (
    <PageContainer>
      <PageTitle>{lang === 'ta' ? 'அபிஜித் முஹூர்த்தம்' : 'Abhijit Muhurta'}</PageTitle>
      <PageSubtitle>{lang === 'ta' ? 'நாளின் மிகச்சிறந்த சுப நேரம்' : 'The Golden Window - Most Auspicious Time of the Day'}</PageSubtitle>

      <div style={{ textAlign: 'right', marginBottom: 10 }}>
        <Select value={lang} onChange={e => setLang(e.target.value)} style={{ width: 'auto', padding: '6px 10px' }}>
          {Object.entries(langLabels).map(([code, label]) => (
            <option key={code} value={code}>{label}</option>
          ))}
        </Select>
      </div>

      {!result ? (
        <Card style={{ maxWidth: 500, margin: '0 auto' }}>
          <CardTitle>{lang === 'ta' ? 'இடம் & தேதி' : 'Location & Date'}</CardTitle>
          <SharedLocationForm
            placeValue={place}
            lat={lat}
            lon={lon}
            timezone={timezone}
            onPlaceChange={handlePlaceChange}
            showDate
            date={date}
            onDateChange={setDate}
            onSubmit={fetchMuhurta}
            submitLabel={lang === 'ta' ? 'முஹூர்த்தம் காட்டு' : 'Show Muhurta'}
            loading={loading}
            error={error}
            pageName="abhijitMuhurta"
            lang={lang}
          />

          <div style={{ marginTop: 24, padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
            <p style={{ color: '#555', lineHeight: 1.7, fontSize: '0.9rem', margin: 0 }}>
              {lang === 'ta'
                ? 'அபிஜித் முஹூர்த்தம் என்பது நாளின் நடுப்பகுதியில் வரும் மிகவும் சுபமான நேரம். இது சூரிய உதயம் மற்றும் அஸ்தமனத்தை அடிப்படையாகக் கொண்டு கணக்கிடப்படுகிறது. எந்த நல்ல காரியத்தையும் இந்த நேரத்தில் தொடங்கலாம்.'
                : 'Abhijit Muhurta is the most auspicious time of the day, occurring around midday. It is calculated based on sunrise and sunset. This is considered universally auspicious for starting any good activity - no other factors need to be checked.'}
            </p>
          </div>
        </Card>
      ) : (
        <>
          <BackButton onClick={() => { setResult(null); setCountdown(null); }}>
            {lang === 'ta' ? 'புதிய தேடல்' : 'New Search'}
          </BackButton>

          {/* Golden Window */}
          <GoldenCard>
            <GoldenIcon>&#9728;</GoldenIcon>
            <GoldenTitle>{lang === 'ta' ? 'அபிஜித் முஹூர்த்தம்' : 'Abhijit Muhurta'}</GoldenTitle>
            <GoldenTime>{startTime} - {endTime}</GoldenTime>
            {duration && <GoldenDuration>{lang === 'ta' ? 'கால அளவு' : 'Duration'}: {duration}</GoldenDuration>}
            <GoldenNote>
              {lang === 'ta'
                ? 'அனைத்து சுபச் செயல்களுக்கும் மிகவும் பொருத்தமான நேரம்'
                : 'Universally auspicious - ideal for starting any important activity'}
            </GoldenNote>
          </GoldenCard>

          {/* Countdown */}
          {countdown && countdown.status !== 'passed' && (
            <CountdownCard style={countdown.status === 'active' ? { background: '#e8f5e9', borderColor: '#4caf50' } : {}}>
              <CountdownLabel>{countdown.label}</CountdownLabel>
              {countdown.time && (
                <CountdownTime style={countdown.status === 'active' ? { color: '#4caf50' } : {}}>
                  {countdown.time}
                </CountdownTime>
              )}
            </CountdownCard>
          )}

          {countdown && countdown.status === 'passed' && (
            <div style={{ textAlign: 'center', padding: 16, background: '#f5f5f5', borderRadius: 8, marginTop: 16, color: '#888' }}>
              {countdown.label}
            </div>
          )}

          {/* Sun Info */}
          <InfoGrid>
            <InfoCard color="#ff9800">
              <InfoLabel>{lang === 'ta' ? 'சூரிய உதயம்' : 'Sunrise'}</InfoLabel>
              <InfoValue>{sunrise}</InfoValue>
            </InfoCard>
            <InfoCard color="#ff5722">
              <InfoLabel>{lang === 'ta' ? 'சூரிய அஸ்தமனம்' : 'Sunset'}</InfoLabel>
              <InfoValue>{sunset}</InfoValue>
            </InfoCard>
          </InfoGrid>

          {/* Additional info from result */}
          {result.description && (
            <Card style={{ marginTop: 20 }}>
              <p style={{ color: '#555', lineHeight: 1.7, fontSize: '0.95rem', textAlign: 'center', margin: 0 }}>
                {result.description}
              </p>
            </Card>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default AbhijitMuhurta;
